import React from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import { DefinitionTooltip } from "src/components/common";
import type {
  ConfigureFormState,
  NotificationOptionsResponse,
  NotificationSection,
  NotificationSettingsItem,
  PageTarget,
  PaidMediaCampaign,
  SalesProduct,
  SocialPlatformData,
} from "../Types/NotificationTypes";
import {
  PAID_MEDIA_METRIC_PLATFORM_RESTRICTIONS,
  PLATFORM_LABELS,
} from "../Utils/NotificationConstants";
import {
  buildCampaignData,
  buildCityOptions,
  buildPageData,
  buildProductData,
  getComparisonOptionsForItem,
  getPanelValidity,
  getPeriodOptionsForItem,
  getSaveButtonLabel,
  hasConditionAndValueForKind,
  isEditingExistingRule,
  isNegativeThresholdAllowed,
  pageKey,
  usesComparison,
} from "../Utils/NotificationRuleHelpers";
import "../Styles/ConfigureThresholdPanel.scss";

interface ConfigureThresholdPanelProps {
  section: NotificationSection;
  item: NotificationSettingsItem;
  sectionIdx: number;
  itemIdx: number;
  form: ConfigureFormState;
  options: NotificationOptionsResponse | null;
  saving: boolean;
  availablePlatforms: { key: string; label: string }[];
  availableCampaigns: PaidMediaCampaign[];
  socialPlatforms: SocialPlatformData[];
  availableCities: string[];
  cityAvailable: boolean;
  citySearch: string;
  availableProducts: SalesProduct[];
  productAvailable: boolean;
  productSearch: string;
  configurePanelRef: React.RefObject<HTMLTableRowElement>;
  setForm: React.Dispatch<React.SetStateAction<ConfigureFormState>>;
  onClose: () => void;
  onSave: () => void;
  onPlatformsChange: (_val: unknown) => void;
  onCampaignsChange: (_val: unknown, _allCampaigns: PaidMediaCampaign[]) => void;
  onCampaignSearch: (_val: string) => void;
  onSocialPlatformsChange: (_val: unknown) => void;
  onTargetsChange: (_val: unknown, _availablePages: PageTarget[]) => void;
  onCitiesChange: (_val: unknown) => void;
  onCitySearch: (_val: string) => void;
  onProductsChange: (_val: unknown, _allProducts: SalesProduct[]) => void;
  onProductSearch: (_val: string) => void;
}

const ConfigureThresholdPanel: React.FC<ConfigureThresholdPanelProps> = ({
  section,
  item,
  sectionIdx,
  itemIdx,
  form,
  options,
  saving,
  availablePlatforms,
  availableCampaigns,
  socialPlatforms,
  availableCities,
  cityAvailable,
  citySearch,
  availableProducts,
  productAvailable,
  productSearch,
  configurePanelRef,
  setForm,
  onClose,
  onSave,
  onPlatformsChange,
  onCampaignsChange,
  onCampaignSearch,
  onSocialPlatformsChange,
  onTargetsChange,
  onCitiesChange,
  onCitySearch,
  onProductsChange,
  onProductSearch,
}) => {
  const { t } = useTranslation();

  const isEdit = isEditingExistingRule(item);
  const periodOptions = getPeriodOptionsForItem(section.system, item, options);
  const comparisonOptions = getComparisonOptionsForItem(section.system, item, options);

  const showComparison = usesComparison(item);
  const hasConditionAndValue = hasConditionAndValueForKind(item.kind);
  const isBetween = form.operator === "between";
  const allowNegativeThreshold = isNegativeThresholdAllowed(section.system, item.key);

  const isRequiredFieldsValid = getPanelValidity(section, item, form);
  const saveButtonLabel = getSaveButtonLabel(saving, isEdit, t);
  const availablePlatformLabelByKey = new Map(availablePlatforms.map(p => [p.key, p.label]));
  const restrictedPlatformKeys = PAID_MEDIA_METRIC_PLATFORM_RESTRICTIONS[item.key];
  const platformOptions = restrictedPlatformKeys?.length
    ? restrictedPlatformKeys.map(key => ({
        value: key,
        label: availablePlatformLabelByKey.get(key) ?? PLATFORM_LABELS[key] ?? key,
      }))
    : availablePlatforms.map(p => ({
        value: p.key,
        label: p.label,
      }));

  const hasNoPlatformOptions = platformOptions.length === 0;
  const socialPlatformOptions = socialPlatforms.map(sp => ({
    value: sp.key,
    label: sp.label,
  }));

  const { map: campaignMap, options: campaignOptions } = buildCampaignData(
    form.campaigns,
    availableCampaigns
  );
  const { map: pageMap, options: pageOptions } = buildPageData(
    socialPlatforms,
    form.targets,
    form.social_platforms
  );
  const cityOptions = buildCityOptions(form.cities, availableCities);
  const { map: productMap, options: productOptions } = buildProductData(
    form.products,
    availableProducts
  );

  return (
    <tr
      className="configure-panel-row"
      key={`cfg-${sectionIdx}-${itemIdx}`}
      ref={configurePanelRef}
    >
      <td colSpan={5}>
        <div className="configure-threshold-panel">
          <div className="configure-panel-header">
            <h4 className="configure-panel-title">
              {isEdit
                ? t("notificationPreferences.configureThreshold.edit", {
                    label: item.label,
                    defaultValue: `Edit: ${item.label}`,
                  })
                : t("notificationPreferences.configureThreshold.title", {
                    defaultValue: "Configure Threshold",
                  })}
            </h4>
            <button
              className="configure-panel-close"
              onClick={onClose}
              title={t("notificationPreferences.configureThreshold.close", {
                defaultValue: "Close",
              })}
            >
              <CloseOutlined />
            </button>
          </div>
          <div className="threshold-form-grid">

            {/* PAID MEDIA — Platforms (multi-select dropdown) */}
            {section.system === "paid_media" && (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.platform", {
                    defaultValue: "Platform",
                  })}
                  <span className="required-asterisk">*</span>
                </label>
                {hasNoPlatformOptions ? (
                  <span className="threshold-field-empty-note">
                    {t("notificationPreferences.configureThreshold.noPlatformsAvailable", {
                      defaultValue:
                        "No connected platform supports this metric yet. Connect the required ad account to configure it.",
                    })}
                  </span>
                ) : (
                  <StyledDropdown
                    mode="multiple"
                    value={form.platforms}
                    options={platformOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.selectPlatform",
                      { defaultValue: "Select platform(s)" }
                    )}
                    onChange={onPlatformsChange}
                    maxTagCount="responsive"
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                )}
              </div>
            )}

            {/* PAID MEDIA — Campaigns (multi-select, searchable, threshold only) */}
            {section.system === "paid_media" && item.kind === "threshold" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.campaigns", {
                    defaultValue: "Campaign",
                  })}
                </label>
                <StyledDropdown
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  disabled={form.platforms.length === 0}
                  value={form.campaigns.map(c => c.campaign_id)}
                  options={campaignOptions}
                  placeholder={t(
                    "notificationPreferences.configureThreshold.searchCampaigns",
                    { defaultValue: "Search campaigns…" }
                  )}
                  onSearch={val => onCampaignSearch(val)}
                  onChange={val =>
                    onCampaignsChange(val, Array.from(campaignMap.values()))
                  }
                  maxTagCount="responsive"
                  maxTagTextLength={16}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* SOCIAL — Platforms (multi-select; drives the Pages dropdown) */}
            {section.system === "social" && (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.platform", {
                    defaultValue: "Platform",
                  })}
                  <span className="required-asterisk">*</span>
                </label>
                <StyledDropdown
                  mode="multiple"
                  value={form.social_platforms}
                  options={socialPlatformOptions}
                  placeholder={t(
                    "notificationPreferences.configureThreshold.selectPlatform",
                    { defaultValue: "Select platform(s)" }
                  )}
                  onChange={onSocialPlatformsChange}
                  maxTagCount="responsive"
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* SOCIAL — Page targets (filtered by selected platform) */}
            {section.system === "social" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.pages", {
                    defaultValue: "Pages",
                  })}
                </label>
                <StyledDropdown
                  mode="multiple"
                  disabled={form.social_platforms.length === 0}
                  value={form.targets.map(tgt => pageKey(tgt.platform, tgt.page_id))}
                  options={pageOptions}
                  placeholder={t(
                    "notificationPreferences.configureThreshold.selectPages",
                    { defaultValue: "Select page(s)" }
                  )}
                  onChange={val =>
                    onTargetsChange(val, Array.from(pageMap.values()))
                  }
                  maxTagCount="responsive"
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* WEB ANALYTICS — Cities (multi-select, searchable) */}
            {section.system === "web_analytics" && item.kind === "city" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.cities", {
                    defaultValue: "Cities",
                  })}
                </label>
                {!cityAvailable && form.cities.length === 0 && !citySearch.trim() ? (
                  <span className="unavailable-note">
                    {t(
                      "notificationPreferences.configureThreshold.citiesUnavailable",
                      { defaultValue: "City data not available" }
                    )}
                  </span>
                ) : (
                  <StyledDropdown
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    value={form.cities}
                    options={cityOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.searchCities",
                      { defaultValue: "Search cities…" }
                    )}
                    onSearch={val => onCitySearch(val)}
                    onChange={onCitiesChange}
                    maxTagCount="responsive"
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                )}
              </div>
            )}

            {/* SALES — Cities (multi-select, searchable, optional scope) */}
            {section.system === "sales" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.cities", {
                    defaultValue: "Cities",
                  })}
                </label>
                {!cityAvailable && form.cities.length === 0 && !citySearch.trim() ? (
                  <span className="unavailable-note">
                    {t(
                      "notificationPreferences.configureThreshold.citiesUnavailable",
                      { defaultValue: "City data not available" }
                    )}
                  </span>
                ) : (
                  <StyledDropdown
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    value={form.cities}
                    options={cityOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.searchCities",
                      { defaultValue: "Search cities…" }
                    )}
                    onSearch={val => onCitySearch(val)}
                    onChange={onCitiesChange}
                    maxTagCount="responsive"
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                )}
              </div>
            )}

            {/* SALES — Products (multi-select, searchable, optional scope) */}
            {section.system === "sales" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.products", {
                    defaultValue: "Products",
                  })}
                </label>
                {!productAvailable && form.products.length === 0 && !productSearch.trim() ? (
                  <span className="unavailable-note">
                    {t(
                      "notificationPreferences.configureThreshold.productsUnavailable",
                      { defaultValue: "Product data not available" }
                    )}
                  </span>
                ) : (
                  <StyledDropdown
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    value={form.products.map(p => p.product_id)}
                    options={productOptions}
                    placeholder={t(
                      "notificationPreferences.configureThreshold.searchProducts",
                      { defaultValue: "Search products…" }
                    )}
                    onSearch={val => onProductSearch(val)}
                    onChange={val =>
                      onProductsChange(val, Array.from(productMap.values()))
                    }
                    maxTagCount="responsive"
                    maxTagTextLength={16}
                    className="filter-dropdown"
                    popupClassName="filter-dropdown-menu"
                  />
                )}
              </div>
            )}


            {showComparison ? (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.comparison", {
                    defaultValue: "Comparison",
                  })}
                  <span className="required-asterisk">*</span>
                </label>
                <StyledDropdown
                  value={form.comparison_period}
                  onChange={val =>
                    setForm(f => ({ ...f, comparison_period: val as string }))
                  }
                  options={comparisonOptions}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            ) : (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.period", {
                    defaultValue: "Period",
                  })}
                  <span className="required-asterisk">*</span>
                </label>
                <StyledDropdown
                  value={form.period}
                  onChange={val => setForm(f => ({ ...f, period: val as string }))}
                  options={periodOptions}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* Condition (operator) */}
            {hasConditionAndValue && (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.condition", {
                    defaultValue: "Condition",
                  })}
                  <span className="required-asterisk">*</span>
                </label>
                <StyledDropdown
                  value={form.operator}
                  onChange={val => setForm(f => ({ ...f, operator: val as string }))}
                  options={options?.operators ?? []}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* Threshold type — follower_growth only */}
            {section.system === "social" && item.key === "follower_growth" && (
              <div className="threshold-field threshold-field--optional">
                <label className="threshold-label">
                  {t(
                    "notificationPreferences.configureThreshold.thresholdType",
                    { defaultValue: "Threshold Type" }
                  )}
                </label>
                <StyledDropdown
                  value={form.threshold_type}
                  onChange={val =>
                    setForm(f => ({ ...f, threshold_type: val as string }))
                  }
                  options={options?.threshold_types ?? []}
                  className="filter-dropdown"
                  popupClassName="filter-dropdown-menu"
                />
              </div>
            )}

            {/* Value (lower bound when operator is "between") */}
            {hasConditionAndValue && (
              <div className="threshold-field">
                <label className="threshold-label">
                  {isBetween
                    ? t("notificationPreferences.configureThreshold.lowerValue", {
                        defaultValue: "Lower Value",
                      })
                    : t("notificationPreferences.configureThreshold.value", {
                        defaultValue: "Value",
                      })}
                  {item.unit === "percentage" && " (%)"}
                  {item.unit === "currency" && (
                    <> ({t("notificationPreferences.configureThreshold.unitCurrency", { defaultValue: "Currency" })})</>
                  )}
                  {item.unit === "seconds" && (
                    <> ({t("notificationPreferences.configureThreshold.unitSeconds", { defaultValue: "Sec" })})</>
                  )}
                  <span className="required-asterisk">*</span>
                  {item.unit === "currency" && (
                    <DefinitionTooltip
                      label={t("notificationPreferences.configureThreshold.value", {
                        defaultValue: "Value",
                      })}
                      info={{
                        description: t(
                          "notificationPreferences.configureThreshold.valueCurrencyTooltip",
                          {
                            defaultValue:
                              "Enter the threshold amount in your Shopify store's configured currency. The alert will trigger when the selected metric meets this value.",
                          }
                        ),
                      }}
                    />
                  )}
                </label>
                <Input
                  type="number"
                  min={allowNegativeThreshold ? undefined : 0}
                  value={form.threshold_value}
                  onChange={e => {
                    const next = allowNegativeThreshold
                      ? e.target.value
                      : e.target.value.replaceAll("-", "");
                    setForm(f => ({ ...f, threshold_value: next }));
                  }}
                  className="threshold-value-input"
                  placeholder={t("notificationPreferences.configureThreshold.valuePlaceholder", {
                    defaultValue: "e.g. 1000",
                  })}
                />
              </div>
            )}

            {hasConditionAndValue && isBetween && (
              <div className="threshold-field">
                <label className="threshold-label">
                  {t("notificationPreferences.configureThreshold.upperValue", {
                    defaultValue: "Upper Value",
                  })}
                  {item.unit === "percentage" && " (%)"}
                  {item.unit === "currency" && " (Currency)"}
                  {item.unit === "seconds" && " (Sec)"}
                  <span className="required-asterisk">*</span>
                </label>
                <Input
                  type="number"
                  min={allowNegativeThreshold ? undefined : 0}
                  value={form.threshold_value_upper}
                  onChange={e => {
                    const next = allowNegativeThreshold
                      ? e.target.value
                      : e.target.value.replaceAll("-", "");
                    setForm(f => ({ ...f, threshold_value_upper: next }));
                  }}
                  className="threshold-value-input"
                  placeholder={t("notificationPreferences.configureThreshold.upperValuePlaceholder", {
                    defaultValue: "e.g. 5000",
                  })}
                />
              </div>
            )}

          </div>

          <div className="threshold-actions">
            <button
              className="threshold-cancel-btn button button--outline"
              onClick={onClose}
            >
              {t("notificationPreferences.configureThreshold.cancel", {
                defaultValue: "Cancel",
              })}
            </button>
            <StyledTooltip
              content={
                !saving && !isRequiredFieldsValid
                  ? t(
                      "notificationPreferences.configureThreshold.requiredFieldsTooltip",
                      { defaultValue: "Complete all required fields" }
                    )
                  : ""
              }
              placement="top"
            >
              <button
                className="threshold-save-btn button button--Primary"
                onClick={onSave}
                disabled={saving || !isRequiredFieldsValid}
              >
                {saveButtonLabel}
              </button>
            </StyledTooltip>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default ConfigureThresholdPanel;
